import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { loadPresentation, placeLabel } from '../../domain/places/presentation';

const styles = {
  shell: {
    position: 'absolute',
    zIndex: 5,
    top: { xs: 'calc(max(10px, env(safe-area-inset-top)) + 54px)', sm: 22 },
    left: '50%',
    width: { xs: 'calc(100vw - 20px)', sm: 'min(620px, calc(100vw - 260px))' },
    transform: 'translateX(-50%)',
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.5, sm: 1 },
    minHeight: { xs: 52, sm: 58 },
    p: { xs: '5px 6px 5px 11px', sm: '7px 8px 7px 16px' },
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: { xs: '16px', sm: '20px' },
    bgcolor: 'background.paper',
    boxShadow: '0 18px 60px rgba(17,39,35,.18)',
    backdropFilter: 'blur(18px)',
  },
  input: {
    flex: 1,
    minWidth: 0,
    '& .MuiInputBase-root': { fontSize: { xs: 15, sm: 17 }, fontWeight: 650 },
    '& input': { color: 'text.primary', textAlign: 'start', minWidth: 0 },
  },
  heading: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1,
    p: { xs: '4px 4px 10px', sm: '4px 7px 12px' },
    '& span': { color: 'text.secondary', fontSize: 12 },
  },
  row: {
    width: '100%',
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
    p: '8px 9px',
    borderRadius: '14px',
    color: 'text.primary',
    justifyContent: 'flex-start',
    textAlign: 'start',
    touchAction: 'manipulation',
    '&:hover': { bgcolor: 'action.hover' },
  },
  copy: {
    minWidth: 0,
    flex: 1,
    gap: 0.3,
    alignItems: 'flex-start',
    '& strong, & small': {
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& small': { color: 'text.secondary' },
  },
};

export default function SearchOverlay({
  query,
  onQueryChange,
  onSubmit,
  onClear,
  searching,
  results,
  selectedPlace,
  onSelect,
  hidden = false,
}) {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 599.95px), (max-height: 499.95px)');
  const [resultsOpen, setResultsOpen] = useState(false);

  useEffect(() => {
    setResultsOpen(results.length > 1 && !selectedPlace);
  }, [results, selectedPlace]);

  return (
    <>
      <Stack
        component="section"
        aria-hidden={hidden}
        sx={{
          ...styles.shell,
          opacity: hidden ? 0 : 1,
          transform: hidden
            ? 'translate(-50%, -10px) scale(.985)'
            : 'translate(-50%, 0) scale(1)',
          pointerEvents: hidden ? 'none' : 'auto',
          transition: 'opacity 220ms ease, transform 300ms cubic-bezier(.2,.8,.2,1)',
        }}
      >
        <Paper component="form" elevation={8} sx={styles.bar} onSubmit={onSubmit}>
          <SearchRoundedIcon sx={{ color: 'text.secondary', fontSize: { xs: 24, sm: 28 }, transform: 'rotate(-20deg)' }} />
          <TextField
            sx={styles.input}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={t('search.placeholder')}
            variant="standard"
            slotProps={{ input: { disableUnderline: true, 'aria-label': t('search.placeholder') } }}
          />
          {(query || results.length > 0) && (
            <IconButton
              sx={{ width: 36, height: 36, flex: '0 0 auto', color: 'text.secondary', bgcolor: 'action.hover' }}
              onClick={onClear}
              aria-label={t('search.clear')}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}
          <Button
            sx={{
              minWidth: { xs: 54, sm: 72 },
              minHeight: 42,
              px: { xs: 1.25, sm: 2.5 },
              borderRadius: '13px',
              color: 'white',
              background: 'linear-gradient(135deg, #2888ff, #1269e8)',
            }}
            type="submit"
            variant="contained"
            disabled={searching}
          >
            {searching ? <CircularProgress size={20} color="inherit" /> : t('search.button')}
          </Button>
        </Paper>
      </Stack>

      <SwipeableDrawer
        key={isMobile ? 'mobile-results' : 'desktop-results'}
        anchor={isMobile ? 'bottom' : 'left'}
        open={resultsOpen}
        onOpen={() => setResultsOpen(true)}
        onClose={() => setResultsOpen(false)}
        disableSwipeToOpen
        ModalProps={{
          keepMounted: true,
          hideBackdrop: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              pointerEvents: 'auto',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
            },
          },
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiDrawer-paper': {
            pointerEvents: 'auto',
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
            top: isMobile ? 'auto' : 96,
            bottom: 0,
            width: isMobile ? '100%' : 370,
            height: isMobile ? 'min(58dvh, 520px)' : '70%',
            maxHeight: isMobile ? 'calc(100dvh - 128px)' : 'none',
            p: { xs: 1.25, sm: 1.5 },
            pb: { xs: 'max(10px, env(safe-area-inset-bottom))', sm: 1.5 },
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            borderBottom: isMobile ? 0 : undefined,
            borderRadius: isMobile ? '22px 22px 0 0' : '0 20px 20px 0',
            bgcolor: 'background.paper',
            boxShadow: '0 18px 60px rgba(17,39,35,.18)',
            backdropFilter: 'blur(18px)',
          },
        }}
      >
        <Stack sx={{ height: '100%', minHeight: 0, pointerEvents: 'auto' }}>
          {isMobile && (
            <Box sx={{ width: 48, height: 5, mx: 'auto', mb: 1, borderRadius: 99, bgcolor: 'text.disabled' }} />
          )}
          <Stack direction="row" sx={styles.heading}>
            <Stack sx={{ minWidth: 0 }}>
              <Typography component="strong" fontWeight={800}>
                {t('search.results', { count: results.length })}
              </Typography>
              <Typography component="span">{t('search.rankedBy')}</Typography>
            </Stack>
            <IconButton size="small" onClick={() => setResultsOpen(false)} aria-label={t('search.closeResults')}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack sx={{ minHeight: 0, overflowY: 'auto', px: { xs: 0.25, sm: 1 }, WebkitOverflowScrolling: 'touch' }}>
            {results.map((place) => {
              const load = loadPresentation(place, t);
              return (
                <Button sx={styles.row} key={place.id} onClick={() => onSelect(place)}>
                  <Box sx={{
                    width: 18, height: 18, flex: '0 0 auto', border: '3px solid white',
                    borderRadius: '50%', boxShadow: '0 0 0 1px rgba(0,0,0,.08)',
                    bgcolor: load.color,
                  }} />
                  <Stack sx={styles.copy}>
                    <Typography component="strong" fontWeight={800}>{placeLabel(place)}</Typography>
                    <Typography component="small">{place.address || load.label}</Typography>
                  </Stack>
                  <Typography component="span" sx={{ color: 'text.secondary', fontSize: 27 }}>
                    {i18n.dir() === 'rtl' ? '‹' : '›'}
                  </Typography>
                </Button>
              );
            })}
          </Stack>
        </Stack>
      </SwipeableDrawer>
    </>
  );
}
